import Papa from 'papaparse'
import { CSV_TEMPLATE_HEADERS, CSV_COLUMN_INFO } from './constants.js'
import { downloadFile, formatDateOnly } from './utils.js'

// Records are projected through the canonical header list so adding a census
// field can never shift the example values into the wrong columns.
const CSV_TEMPLATE_EXAMPLE_ROWS = [
  {
    id: 'EMP001', name: 'John Smith', dob: '01/15/1985', gender: 'M', hire_date: '03/01/2020',
    hours_served: '2080', compensation: '85000', prior_year_compensation: '80000',
    pretax_percent: '6', roth_percent: '2', balance: '45000', loan_balance: '0',
    status: 'Active', full_time: 'Y', key_employee: 'N', five_percent_owner: 'N', allocation_group: 'Staff',
  },
  {
    id: 'EMP002', dob: '06/22/1990', gender: 'F', hire_date: '01/15/2018', hours_served: '2080',
    compensation: '210000', prior_year_compensation: '198000', pretax_amount: '24500',
    roth_amount: '5000', balance: '950000', loan_balance: '5000', status: 'Active', full_time: 'Y',
    key_employee: 'Y', five_percent_owner: 'Y', allocation_group: 'Owner', after_tax_percent: '10',
  },
  {
    name: 'Bob Wilson', dob: '12/03/1975', tenure: '10y 3m', termination_date: '09/30/2025',
    hours_served: '1500', compensation: '125000', prior_year_compensation: '118000', pretax_percent: '8',
    balance: '180000', loan_balance: '0', status: 'Terminated', full_time: 'N', key_employee: 'N',
    five_percent_owner: 'N', allocation_group: 'Staff',
  },
].map((record) => CSV_TEMPLATE_HEADERS.map((header) => record[header] ?? ''))

export const CSV_TEMPLATE_EXAMPLE_COUNT = CSV_TEMPLATE_EXAMPLE_ROWS.length

export function generateCsvTemplate() {
  const headers = CSV_TEMPLATE_HEADERS.join(',')
  return [headers, ...CSV_TEMPLATE_EXAMPLE_ROWS.map(row => row.join(','))].join('\n')
}

export function downloadCsvTemplate() {
  const content = generateCsvTemplate()
  downloadFile(content, '401k-employee-template.csv', 'text/csv')
}

export function generateInstructionsDocument() {
  const instructions = `401(k) Calculator - Employee Data Import Instructions
=====================================================

This document explains how to prepare your employee data for import into the 401(k) Calculator.

FILE FORMAT
-----------
- Save your file as a CSV (Comma-Separated Values) file
- The first row must contain column headers
- Each subsequent row represents one employee

COLUMN REFERENCE
----------------
${Object.entries(CSV_COLUMN_INFO).map(([col, info]) => 
  `${col.toUpperCase()}${info.required ? ' (Required)' : ' (Optional)'}
  Description: ${info.description}
  Example: ${info.example}
`).join('\n')}

NOTES
-----
1. Dates can be in MM/DD/YYYY or YYYY-MM-DD format
2. Percentages use a 0-100 scale and are read exactly as written: enter 6 (or 6%) for 6%, 3.5 for 3.5%, or 0.5 for half a percent. A trailing % sign and surrounding spaces are fine. Note: 0.06 is read as 0.06%, not 6% - use whole-number percents (values are clamped to 0-100)
3. Currency values can include a $ sign and commas (e.g., $85,000 or 85000); they are stripped automatically. Negative numbers are treated as 0
4. Gender accepts M/Male, F/Female, or U/Undisclosed (plus common variants like Unknown, X, Non-binary); anything unrecognized is treated as blank
5. Status should be Active, Terminated, or Leave (defaults to Active if blank)
6. Status is authoritative. Active and Leave remain non-terminated even when a termination date is populated; that date is ignored with a warning
7. A termination date affects valuation, turnover, and termination proration only when status is Terminated. Unusable dates fall back to plan-year end for valuation with a warning
8. Each employee should have a unique ID (auto-generated if blank)
9. prior_year_compensation is optional. When blank, current compensation is used as an estimated HCE proxy
10. Blank or invalid tenure and hours remain unknown. If those fields are required for eligibility, the employee is modeled as a full participant and labeled Eligibility unknown
11. Use Y or N for key_employee on every employee included in plan metrics. Top-heavy is unavailable when any included record is blank or total balance is zero
12. allocation_group is the allocation class code (e.g. A, B, Owner, Staff). Class-based plan formulas set one rate per class; employees with no code fall under "Everyone else", and a code the plan has no rate for allocates zero and is flagged for review
13. Compensation proration ON expects an annualized salary rate. Proration OFF expects actual plan-year or YTD compensation
14. Eligibility gates the whole plan: an employee who fails the plan's age/service/hours requirements makes no contributions at all (no employee deferrals, no employer money). The one carve-out is the SECURE 2.0 long-term part-time rule (see full_time below)
15. full_time accepts Y/N plus common spellings (FT, PT, Full-Time, Part Time). Part-time employees (N or blank) with 500+ hours and 2+ years of service can still make employee deferrals as long-term part-time participants; they receive no employer contributions

CONTRIBUTION AMOUNTS VS PERCENTAGES
-----------------------------------
You can specify contributions as percentages (pretax_percent, roth_percent) or as dollar amounts (pretax_amount, roth_amount).
- If only percentages are provided, contributions are calculated as a percentage of compensation
- If dollar amounts are provided, they override the percentage values
- You can mix approaches: e.g., use pretax_percent for pre-tax but specify a fixed roth_amount for Roth

EXAMPLE DATA
------------
${generateCsvTemplate()}

Row explanations:
- EMP001: Standard full-time percent-based deferrals (6% pretax + 2% roth) with prior-year compensation
- EMP002: Dollar-amount overrides ($24,500 pretax, $5,000 roth) – percentages are ignored; owner/key with 10% after-tax
- Row 3: Blank ID (auto-generated); tenure override (10y 3m) instead of hire_date; terminated part-time employee with end date

TIPS
----
- Start with the template CSV and replace or edit its example rows
- All optional columns can be left blank
- If pretax and roth contributions (both percent and amount) are all blank or 0, the employee is a non-participant
- Dollar amounts override percentages: if pretax_amount is 5000, that value is used instead of calculating from pretax_percent
`

  return instructions
}

export function downloadInstructions() {
  const content = generateInstructionsDocument()
  downloadFile(content, '401k-import-instructions.txt', 'text/plain')
}

// Common header spellings that map onto a canonical census field. Exported so the
// census-mapping advisor proposes mappings against the same source of truth the
// parser uses. Keys are normalized (lowercase, spaces → underscores).
export const HEADER_ALIASES = {
          'employee_id': 'id',
          'emp_id': 'id',
          'unique_id': 'id',
          'date_of_birth': 'dob',
          'birth_date': 'dob',
          'birthdate': 'dob',
          'sex': 'gender',
          'hire': 'hire_date',
          'start_date': 'hire_date',
          'service': 'tenure',
          'service_length': 'tenure',
          'service_time': 'tenure',
          'service_months': 'tenure',
          'service_years': 'tenure',
          'years_of_service': 'tenure',
          'yos': 'tenure',
          'term_date': 'termination_date',
          'end_date': 'termination_date',
          'termination': 'termination_date',
          'hours': 'hours_served',
          'hours_worked': 'hours_served',
          'service_hours': 'hours_served',
          'salary': 'compensation',
          'annual_salary': 'compensation',
          'comp': 'compensation',
          'pretax': 'pretax_percent',
          'pretax_%': 'pretax_percent',
          'pre_tax': 'pretax_percent',
          'pre_tax_percent': 'pretax_percent',
          'deferral': 'pretax_percent',
          'deferral_percent': 'pretax_percent',
          'pretax_$': 'pretax_amount',
          'pre_tax_amount': 'pretax_amount',
          'pretax_contribution': 'pretax_amount',
          'roth': 'roth_percent',
          'roth_%': 'roth_percent',
          'roth_$': 'roth_amount',
          'roth_contribution': 'roth_amount',
          'account_balance': 'balance',
          'current_balance': 'balance',
          'loan': 'loan_balance',
          'outstanding_loan': 'loan_balance',
          'forfeiture': 'forfeiture_allocation',
          'forfeitures': 'forfeiture_allocation',
          'forfeiture_reallocation': 'forfeiture_allocation',
          'reallocated_forfeiture': 'forfeiture_allocation',
          'forfeiture_amount': 'forfeiture_allocation',
          'qnec': 'qnec_amount',
          'qnecs': 'qnec_amount',
          'qualified_nonelective': 'qnec_amount',
          'qualified_non_elective': 'qnec_amount',
          'employment_status': 'status',
          'emp_status': 'status',
          'fulltime': 'full_time',
          'full-time': 'full_time',
          'ft': 'full_time',
          'ft_pt': 'full_time',
          'ft/pt': 'full_time',
          'full_part_time': 'full_time',
          'full/part_time': 'full_time',
          'full_or_part_time': 'full_time',
          'fte_status': 'full_time',
          'employment_type': 'full_time',
          'emp_type': 'full_time',
          'time_status': 'full_time',
          'work_schedule': 'full_time',
          'prior_comp': 'prior_year_compensation',
          'py_comp': 'prior_year_compensation',
          'prior_year_salary': 'prior_year_compensation',
          'prior_year_comp': 'prior_year_compensation',
          'prev_comp': 'prior_year_compensation',
          'cash_balance_entry_date': 'cb_participant_entry_date',
          'cb_entry_date': 'cb_participant_entry_date',
          'plan_entry_date': 'cb_participant_entry_date',
          'participation_date': 'cb_participant_entry_date',
          'cash_balance_opening_balance': 'cb_opening_balance',
          'cb_opening_account_balance': 'cb_opening_balance',
          'prior_accrued_monthly_benefit': 'cb_prior_accrued_monthly_benefit',
          'cb_prior_monthly_benefit': 'cb_prior_accrued_monthly_benefit',
          'prior_cb_vesting_years': 'cb_prior_vesting_years',
          'high_3_comp': 'high_3_compensation',
          'high_3_pay': 'high_3_compensation',
          'highest_3_year_comp': 'high_3_compensation',
          'highest_3_year_average_compensation': 'high_3_compensation',
          '415_comp': 'section_415_compensation',
          'section_415_comp': 'section_415_compensation',
          'section_415_compensation': 'section_415_compensation',
          'key': 'key_employee',
          'key_ee': 'key_employee',
          'is_key': 'key_employee',
          'owner': 'five_percent_owner',
          'is_owner': 'five_percent_owner',
          '5%_owner': 'five_percent_owner',
          '5_percent_owner': 'five_percent_owner',
          'owner_5_percent': 'five_percent_owner',
          'five_pct_owner': 'five_percent_owner',
          'more_than_5%_owner': 'five_percent_owner',
          'key_owner': 'five_percent_owner',
          'ownership_%': 'ownership_percent',
          '%_owned': 'ownership_percent',
          'percent_owned': 'ownership_percent',
          'pct_owned': 'ownership_percent',
          'ownership': 'ownership_percent',
          'own_%': 'ownership_percent',
          'family_member_of': 'family_of',
          'family_aggregation_with': 'family_of',
          'related_to_owner': 'family_of',
          'date_of_hire': 'hire_date',
          'date_of_termination': 'termination_date',
          'hours_of_service': 'hours_served',
          'gross_compensation': 'compensation',
          'gross_comp': 'compensation',
          'considered_earnings': 'compensation',
          // Split-name censuses (advisor spreadsheets routinely carry Last /
          // First columns); buildCensusFromRows combines them into `name`.
          'first': 'first_name',
          'fname': 'first_name',
          'given_name': 'first_name',
          'last': 'last_name',
          'lname': 'last_name',
          'surname': 'last_name',
          'employee_name_last': 'last_name',
          'employee_last_name': 'last_name',
          'employee_first_name': 'first_name',
          'class': 'allocation_group',
          'employee_class': 'allocation_group',
          'allocation_class': 'allocation_group',
          'group': 'allocation_group',
          'aftertax': 'after_tax_percent',
          'after_tax': 'after_tax_percent',
          'after_tax_%': 'after_tax_percent',
          'after_tax_$': 'after_tax_amount',
          'aftertax_amount': 'after_tax_amount',
          'after_tax_contribution': 'after_tax_amount',
}

// Normalize a raw CSV header to a canonical census field. `headerOverrides` (keyed
// by normalized header) wins over the built-in aliases – that is how a confirmed
// census-mapping is applied on re-import without touching any row data.
export function normalizeHeader(header, headerOverrides = {}) {
  const normalized = String(header).toLowerCase().trim().replace(/\s+/g, '_')
  if (headerOverrides[normalized]) return headerOverrides[normalized]
  return HEADER_ALIASES[normalized] || normalized
}

// Whether a normalized header resolves to a field the census import understands.
// Used by the spreadsheet header-row sniffer to score candidate header rows.
const KNOWN_CENSUS_FIELDS = new Set([
  ...CSV_TEMPLATE_HEADERS,
  ...Object.values(HEADER_ALIASES),
])
export function isKnownCensusField(field) {
  return KNOWN_CENSUS_FIELDS.has(field)
}

// Build the census import result from parsed row objects whose keys are already
// normalized census fields. Shared by the quick CSV path (parseCsvFile) and the
// Import Studio (parseCensusRows) so both validate rows identically.
function tenureUnitFromHeader(header) {
  const normalized = String(header || '').toLowerCase().replace(/[_-]+/g, ' ')
  if (/\b(month|months|mo|mos)\b/.test(normalized)) return 'months'
  if (/\b(year|years|yr|yrs|yos)\b/.test(normalized)) return 'years'
  return null
}

function formatTenureFromMonths(totalMonths) {
  const safeMonths = Math.max(0, Math.round(totalMonths))
  const years = Math.floor(safeMonths / 12)
  const months = safeMonths % 12
  const parts = []
  if (years > 0) parts.push(`${years}y`)
  if (months > 0) parts.push(`${months}m`)
  return parts.length ? parts.join(' ') : '0m'
}

function normalizeImportedTenure(value, sourceHeader = '') {
  if (value === null || value === undefined || String(value).trim() === '') return ''
  const text = String(value).trim()
  const tokenPattern = /(\d+(?:\.\d+)?)\s*(y|yr|yrs|year|years|m|mo|mos|month|months|d|day|days)\b/gi
  const matches = [...text.matchAll(tokenPattern)]
  if (matches.length > 0) {
    const unmatched = text.replace(tokenPattern, '').replace(/[\s,]+/g, '')
    if (!unmatched) {
      const totalMonths = matches.reduce((sum, match) => {
        const amount = Number(match[1])
        if (!Number.isFinite(amount)) return sum
        const unit = match[2].toLowerCase()
        if (unit.startsWith('y')) return sum + (amount * 12)
        if (unit.startsWith('d')) return sum + (amount / 30)
        return sum + amount
      }, 0)
      return formatTenureFromMonths(totalMonths)
    }
    return text
  }

  const numericText = text.replace(/,/g, '')
  if (!/^\d+(\.\d+)?$/.test(numericText)) return text
  const parsed = Number(numericText)
  if (!Number.isFinite(parsed)) return text

  const headerUnit = tenureUnitFromHeader(sourceHeader)
  const unit = headerUnit || (numericText.includes('.') ? 'years' : 'months')
  return formatTenureFromMonths(parsed * (unit === 'years' ? 12 : 1))
}

export function buildCensusFromRows(data, originalHeaders = [], { fieldHeaders = {} } = {}) {
  const employees = []
  const skippedRowDetails = []

  // Guarantee every imported employee ends up with a unique ID. A blank ID
  // falls back to EMP00N (row position); any collision (with an explicit ID
  // or another fallback) gets a -2, -3, ... suffix so editing/deleting one
  // row never affects another that happened to share an ID.
  const usedIds = new Set()
  const ensureUniqueId = (base) => {
    let candidate = base
    let suffix = 2
    while (usedIds.has(candidate)) {
      candidate = `${base}-${suffix}`
      suffix += 1
    }
    usedIds.add(candidate)
    return candidate
  }

  data.forEach((row, index) => {
    const rawId = row.id ? String(row.id).trim() : ''
    const fallbackId = `EMP${String(index + 1).padStart(3, '0')}`
    const displayId = rawId || fallbackId
    const status = parseStatus(row.status)
    const pretaxPercent = parseOptionalPercentValue(row.pretax_percent)
    const rothPercent = parseOptionalPercentValue(row.roth_percent)
    const pretaxAmount = parseOptionalNumericValue(row.pretax_amount)
    const rothAmount = parseOptionalNumericValue(row.roth_amount)
    const hoursServed = parseHoursValue(row.hours_served)
    const dobRaw = row.dob
    const dob = parseDateValue(dobRaw)
    const hadDob = dobRaw !== undefined && dobRaw !== null && String(dobRaw).trim() !== ''
    const compensation = parseNumericValue(row.compensation)

    // Check for reasons to skip
    const skipReasons = []
    if (!dob) {
      skipReasons.push(hadDob ? 'invalid date of birth' : 'missing date of birth')
    }
    if (compensation <= 0) {
      skipReasons.push('missing or zero compensation')
    }

    if (skipReasons.length > 0) {
      skippedRowDetails.push({
        rowNumber: index + 2, // +2 because: +1 for 0-index, +1 for header row
        id: displayId,
        reasons: skipReasons,
      })
      return
    }

    employees.push({
      id: ensureUniqueId(rawId || fallbackId),
      name: row.name
        || [row.first_name, row.last_name].map((part) => String(part || '').trim()).filter(Boolean).join(' ')
        || '',
      dob,
      gender: parseGender(row.gender),
      hire_date: parseOptionalDateValue(row.hire_date),
      tenure: normalizeImportedTenure(row.tenure, fieldHeaders.tenure),
      termination_date: parseOptionalDateValue(row.termination_date),
      hours_served: hoursServed,
      compensation,
      prior_year_compensation: parseOptionalNumericValue(row.prior_year_compensation),
      cb_participant_entry_date: parseOptionalDateValue(row.cb_participant_entry_date),
      cb_opening_balance: parseOptionalNumericValue(row.cb_opening_balance),
      cb_prior_accrued_monthly_benefit: parseOptionalNumericValue(row.cb_prior_accrued_monthly_benefit),
      cb_prior_vesting_years: parseOptionalNumericValue(row.cb_prior_vesting_years),
      high_3_compensation: parseOptionalNumericValue(row.high_3_compensation),
      section_415_compensation: parseOptionalNumericValue(row.section_415_compensation),
      pretax_percent: pretaxPercent,
      pretax_amount: pretaxAmount,
      roth_percent: rothPercent,
      roth_amount: rothAmount,
      balance: parseOptionalNumericValue(row.balance),
      loan_balance: parseOptionalNumericValue(row.loan_balance),
      forfeiture_allocation: parseOptionalNumericValue(row.forfeiture_allocation),
      qnec_amount: parseOptionalNumericValue(row.qnec_amount),
      status,
      full_time: parseFullTime(row.full_time),
      key_employee: parseKeyEmployee(row.key_employee),
      five_percent_owner: parseKeyEmployee(row.five_percent_owner),
      ownership_percent: parseOptionalNumericValue(row.ownership_percent),
      family_of: row.family_of ? String(row.family_of).trim() : '',
      allocation_group: row.allocation_group ? String(row.allocation_group).trim() : '',
      after_tax_percent: parseOptionalPercentValue(row.after_tax_percent),
      after_tax_amount: parseOptionalNumericValue(row.after_tax_amount),
    })
  })

  return {
    employees,
    totalRows: data.length,
    validRows: employees.length,
    skippedRows: skippedRowDetails.length,
    skippedRowDetails,
    // Original (pre-normalization) headers, so the census-mapping advisor
    // can propose a mapping for a CSV whose columns did not auto-resolve.
    headers: originalHeaders,
  }
}

// Import Studio path: raw string cells (headers + rows arrays) instead of a File.
// Headers are normalized exactly like parseCsvFile's transformHeader; when two
// columns normalize to the same field, the first non-blank cell wins.
export function parseCensusRows(headers, rows, { headerOverrides = {} } = {}) {
  const fields = headers.map((header) => normalizeHeader(header, headerOverrides))
  const fieldHeaders = {}
  fields.forEach((field, index) => {
    if (field && !(field in fieldHeaders)) fieldHeaders[field] = headers[index]
  })
  const data = rows.map((cells) => {
    const row = {}
    fields.forEach((field, i) => {
      const value = cells[i] === undefined || cells[i] === null ? '' : cells[i]
      if (!(field in row) || String(row[field]).trim() === '') row[field] = value
    })
    return row
  })
  return buildCensusFromRows(data, headers, { fieldHeaders })
}

export function parseCsvFile(file, { headerOverrides = {} } = {}) {
  return new Promise((resolve, reject) => {
    const originalHeaders = []
    const fieldHeaders = {}
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => {
        originalHeaders.push(header)
        const field = normalizeHeader(header, headerOverrides)
        if (field && !(field in fieldHeaders)) fieldHeaders[field] = header
        return field
      },
      complete: (results) => {
        if (results.errors.length > 0) {
          console.warn('CSV parsing warnings:', results.errors)
        }
        resolve(buildCensusFromRows(results.data, originalHeaders, { fieldHeaders }))
      },
      error: (error) => {
        reject(error)
      }
    })
  })
}

// Excel/Google Sheets serial-date support for imports. A bare number in a date
// column (e.g. "43525") is a day count from 1899-12-30; sheets exported without
// cell formats deliver dates this way. 20000..60000 covers 1954-2064 — anything
// outside is far likelier to be a stray number than a date, so it's left alone.
const EXCEL_SERIAL_EPOCH_UTC = Date.UTC(1899, 11, 30)
function excelSerialToIso(value) {
  const normalized = String(value ?? '').trim()
  if (!/^\d{5}(\.\d+)?$/.test(normalized)) return null
  const serial = Number(normalized)
  if (!(serial >= 20000 && serial <= 60000)) return null
  const date = new Date(EXCEL_SERIAL_EPOCH_UTC + Math.round(serial) * 86400000)
  return date.toISOString().slice(0, 10)
}

function parseDateValue(value) {
  return formatDateOnly(excelSerialToIso(value) ?? value)
}

function parseOptionalDateValue(value) {
  if (value === null || value === undefined || String(value).trim() === '') return ''
  const serial = excelSerialToIso(value)
  if (serial) return formatDateOnly(serial) || serial
  return formatDateOnly(value) || String(value).trim()
}

function parseGender(value) {
  if (!value) return ''
  const normalized = String(value).toLowerCase().trim()
  if (['m', 'male', 'man'].includes(normalized)) return 'M'
  if (['f', 'female', 'woman'].includes(normalized)) return 'F'
  if (['u', 'undisclosed', 'unknown', 'x', 'other', 'nonbinary', 'non-binary', 'nb', 'prefer not to say'].includes(normalized)) return 'U'
  // Unrecognized values map to blank rather than being stored verbatim (the
  // gender dropdown can only represent M, F, U, or blank).
  return ''
}

function parseNumericValue(value) {
  if (!value) return 0
  // Accept "$85,000", "85000", "85,000.50" etc.; $, commas, and spaces are stripped.
  const cleaned = String(value).replace(/[$,\s]/g, '')
  const parsed = parseFloat(cleaned)
  if (isNaN(parsed)) return 0
  // These fields are all non-negative (matching the editor's min=0); clamp away
  // accidental negatives instead of importing values the UI would reject.
  return Math.max(0, parsed)
}

// Optional numeric/percent fields keep blanks blank (rather than defaulting to
// 0) so a blank cell stays blank in the table and on a later export.
function parseOptionalNumericValue(value) {
  if (value === null || value === undefined || String(value).trim() === '') return ''
  return parseNumericValue(value)
}

function parseOptionalPercentValue(value) {
  if (value === null || value === undefined || String(value).trim() === '') return ''
  return parsePercentValue(value)
}

function parseHoursValue(value) {
  if (value === null || value === undefined || String(value).trim() === '') return ''
  const normalized = String(value).trim()
  // Advisor censuses write "FT" in the hours column for full-time employees;
  // read it as a standard 2,080-hour year rather than surfacing an error.
  if (/^(ft|full[\s-]?time)$/i.test(normalized)) return 2080
  const parsed = Number(normalized)
  // Keep a non-numeric entry as-is (it surfaces as an "hours invalid" warning);
  // clamp a real number to non-negative.
  return Number.isFinite(parsed) ? Math.max(0, parsed) : normalized
}

function parsePercentValue(value) {
  if (value === null || value === undefined) return 0
  // Percentages are read on a 0–100 scale, exactly as written. We accept the
  // common ways people type a rate – "6", "6%", " 6 % ", "6.5" – but we do NOT
  // guess that a sub-1 value is a decimal fraction: 0.5 means 0.5%, not 50%.
  // (That old guess silently turned half a percent into fifty.)
  const cleaned = String(value).replace(/[%,\s]/g, '')
  if (cleaned === '') return 0
  const parsed = parseFloat(cleaned)
  if (isNaN(parsed)) return 0
  return Math.min(100, Math.max(0, parsed))
}

function parseKeyEmployee(value) {
  if (!value) return ''
  const normalized = String(value).trim().toUpperCase()
  if (normalized === 'Y' || normalized === 'YES' || normalized === 'TRUE' || normalized === '1') return 'Y'
  if (normalized === 'N' || normalized === 'NO' || normalized === 'FALSE' || normalized === '0') return 'N'
  return ''
}

// Payroll exports write the full-time flag many ways (FT/PT, Full-Time,
// part time, F/P); normalize to Y/N and leave anything unrecognized blank.
const FULL_TIME_VALUES = new Set(['Y', 'YES', 'TRUE', '1', 'F', 'FT', 'F/T', 'FULL', 'FULLTIME', 'FULL-TIME', 'FULL_TIME', 'FULL TIME'])
const PART_TIME_VALUES = new Set(['N', 'NO', 'FALSE', '0', 'P', 'PT', 'P/T', 'PART', 'PARTTIME', 'PART-TIME', 'PART_TIME', 'PART TIME', 'PER DIEM', 'PRN'])

function parseFullTime(value) {
  if (!value) return ''
  const normalized = String(value).trim().toUpperCase()
  if (FULL_TIME_VALUES.has(normalized)) return 'Y'
  if (PART_TIME_VALUES.has(normalized)) return 'N'
  return ''
}

function parseStatus(value) {
  if (!value) return 'Active'
  const normalized = String(value).toLowerCase().trim()
  const compact = normalized.replace(/[\s_-]+/g, '')
  if (compact === 'terminated' || compact === 'inactive' || compact === 'term') {
    return 'Terminated'
  }
  if (compact === 'leave' || compact === 'onleave' || compact === 'loa' || compact === 'leaveofabsence') {
    return 'Leave'
  }
  return 'Active'
}

// Exactly mirror what the inputs table shows: a blank field exports blank,
// and a real value (including an explicit 0) exports as-is. This keeps the
// downloaded census a faithful copy of the inputs and round-trips cleanly.
const blankIfEmpty = (value) => (value === '' || value === null || value === undefined ? '' : value)

export function buildEmployeeCsvData(employees, { resolvedDefaultLabels = null } = {}) {
  // No totals/derived rows here – the census export is raw input data only.
  return employees.map(emp => ({
    id: emp.id,
    name: emp.name || '',
    dob: emp.dob || '',
    gender: emp.gender || '',
    hire_date: emp.hire_date ?? '',
    tenure: emp.tenure ?? '',
    termination_date: emp.termination_date ?? '',
    hours_served: blankIfEmpty(emp.hours_served),
    compensation: blankIfEmpty(emp.compensation),
    // The table renders prior-year comp with `|| ''`, so a 0 shows blank there too.
    prior_year_compensation: emp.prior_year_compensation || '',
    cb_participant_entry_date: emp.cb_participant_entry_date ?? '',
    cb_opening_balance: blankIfEmpty(emp.cb_opening_balance),
    cb_prior_accrued_monthly_benefit: blankIfEmpty(emp.cb_prior_accrued_monthly_benefit),
    cb_prior_vesting_years: blankIfEmpty(emp.cb_prior_vesting_years),
    high_3_compensation: blankIfEmpty(emp.high_3_compensation),
    section_415_compensation: blankIfEmpty(emp.section_415_compensation),
    pretax_percent: blankIfEmpty(emp.pretax_percent),
    pretax_amount: blankIfEmpty(emp.pretax_amount),
    roth_percent: blankIfEmpty(emp.roth_percent),
    roth_amount: blankIfEmpty(emp.roth_amount),
    balance: blankIfEmpty(emp.balance),
    loan_balance: blankIfEmpty(emp.loan_balance),
    forfeiture_allocation: blankIfEmpty(emp.forfeiture_allocation),
    qnec_amount: blankIfEmpty(emp.qnec_amount),
    status: emp.status || 'Active',
    full_time: emp.full_time ?? '',
    key_employee: emp.key_employee ?? '',
    five_percent_owner: emp.five_percent_owner ?? '',
    ownership_percent: blankIfEmpty(emp.ownership_percent),
    family_of: emp.family_of ?? '',
    // The active plan's resolved DEFAULT label wins when supplied (§2.2);
    // source-specific membership is not representable in v1 CSV.
    allocation_group: resolvedDefaultLabels?.[emp.id] ?? emp.allocation_group ?? '',
    after_tax_percent: blankIfEmpty(emp.after_tax_percent),
    after_tax_amount: blankIfEmpty(emp.after_tax_amount),
  }))
}

export function exportEmployeesToCsv(employees, { estimate = false, resolvedDefaultLabels = null } = {}) {
  const csv = Papa.unparse(buildEmployeeCsvData(employees, { resolvedDefaultLabels }), {
    columns: CSV_TEMPLATE_HEADERS,
    escapeFormulae: true,
  })

  downloadFile(csv, `401k-employees-export${estimate ? '-ESTIMATE' : ''}.csv`, 'text/csv')
}

export function buildCalculatedCsvData(calculatedEmployees) {
  const formatParticipationStatus = (status) => {
    if (status === 'maxed') return 'Maxed'
    if (status === 'participating') return 'Participating'
    if (status === 'non-participant') return 'Non-participant'
    return status || 'Non-participant'
  }
  const eligibilityLabel = status => ({
    eligible: 'Eligible',
    ineligible: 'Ineligible',
    unknown: 'Unknown',
    met: 'Eligible',
    not_met: 'Ineligible',
    not_applicable: 'Not applicable',
  })[status] || 'Unknown'
  const keyStatusLabel = status => ({
    key: 'Yes',
    'non-key': 'No',
    unknown: 'Unknown',
  })[status] || 'Unknown'
  const fixedOrBlank = value => Number.isFinite(value) ? value.toFixed(2) : ''

  const data = calculatedEmployees.map(emp => ({
    id: emp.id,
    name: emp.name,
    age: emp.age,
    tenure: emp.tenure ? emp.tenure.formatted : '',
    tenure_months: emp.tenure ? emp.tenure.totalMonths : '',
    service_years: emp.serviceYears ?? '',
    hours_served: emp.hoursServed ?? '',
    compensation: emp.compensation,
    cb_participant_entry_date: emp.cashBalanceParticipantEntryDate || '',
    cb_participant_entry_date_source: emp.cashBalanceParticipantEntryDateSource || 'unknown',
    cb_opening_balance: fixedOrBlank(emp.cashBalanceOpeningBalance),
    cb_prior_accrued_monthly_benefit: fixedOrBlank(emp.cashBalancePriorAccruedMonthlyBenefit),
    cb_prior_vesting_years: fixedOrBlank(emp.cashBalancePriorVestingYears),
    high_3_compensation: fixedOrBlank(emp.high3Compensation),
    high_3_compensation_source: emp.high3CompensationSource || 'unknown',
    section_415_compensation: fixedOrBlank(emp.section415Compensation),
    section_415_compensation_source: emp.section415CompensationSource || 'unknown',
    balance: emp.balance,
    loan_balance: emp.loanBalance,
    capped_compensation: emp.cappedCompensation,
    is_hce: emp.isHCE ? 'Yes' : 'No',
    hce_is_estimated: emp.hceIsEstimated ? 'Yes' : 'No',
    is_key_employee: keyStatusLabel(emp.keyEmployeeStatus),
    allocation_group: emp.allocationGroupLabel || '',
    comp_prorated: emp.compWasProrated ? 'Yes' : 'No',
    employment_status: emp.status || 'Active',
    data_warnings: (emp.warnings || []).join('; '),
    is_terminated: emp.isTerminated ? 'Yes' : 'No',
    eligibility_status: eligibilityLabel(emp.eligibility.status),
    meets_age_requirement: eligibilityLabel(emp.eligibility.requirements.age),
    meets_service_requirement: eligibilityLabel(emp.eligibility.requirements.service),
    meets_hours_requirement: eligibilityLabel(emp.eligibility.requirements.hours),
    pretax_percent: emp.pretaxPercent.toFixed(2),
    roth_percent: emp.rothPercent.toFixed(2),
    total_deferral_percent: emp.totalDeferralPercent.toFixed(2),
    pretax_contribution: emp.actualPretax.toFixed(2),
    roth_contribution: emp.actualRoth.toFixed(2),
    catchup_eligible: emp.catchupEligible ? 'Yes' : 'No',
    catchup_type: emp.catchupType,
    catchup_contribution: emp.catchupContribution.toFixed(2),
    total_employee_contribution: emp.totalEmployeeContribution.toFixed(2),
    // Employer contribution breakdown
    employer_match: emp.matchAmount.toFixed(2),
    match_group: emp.matchMode === 'grouped'
      ? (emp.matchGroupMatched ? (emp.matchGroupLabel || 'Everyone else') : 'No group')
      : '',
    employer_nec: emp.necAmount.toFixed(2),
    employer_qnec: (emp.qnecAmount || 0).toFixed(2),
    allocated_forfeiture: (emp.forfeitureAllocation || 0).toFixed(2),
    employer_profit_sharing: emp.profitSharingAmount.toFixed(2),
    age_weighted_profit_sharing: (emp.ageWeightedProfitSharingAmount || 0).toFixed(2),
    age_weighted_testing_age: emp.ageWeightedTestingAge ?? '',
    age_weighted_factor: Number.isFinite(emp.ageWeightedFactor) ? emp.ageWeightedFactor.toFixed(8) : '',
    profit_sharing_component_breakdown: JSON.stringify((emp.profitSharingComponents || []).map((component) => ({
      id: component.componentId, type: component.type, requested: component.requested,
      allocated: component.allocated, weight: component.weight ?? null, factor: component.factor ?? null,
      testingAge: component.testingAge ?? null, capLimited: Boolean(component.capLimited),
      group: component.groupLabel ?? null,
    }))),
    profit_sharing_group: emp.profitSharingGroupMatched
      ? (emp.profitSharingGroupLabel || 'Everyone else')
      : '',
    profit_sharing_415c_limited: emp.profitSharingLimitedBy415c ? 'Yes' : '',
    employer_total: emp.dcEmployerCost.toFixed(2),
    // Vesting
    vesting_enabled: emp.vestingEnabled ? 'Yes' : 'No',
    vested_match_percent: fixedOrBlank(emp.vestedMatchPercent),
    vested_nec_percent: fixedOrBlank(emp.vestedNecPercent),
    vested_profit_sharing_percent: fixedOrBlank(emp.vestedProfitSharingPercent),
    vested_match: fixedOrBlank(emp.vestedMatch),
    vested_nec: fixedOrBlank(emp.vestedNec),
    vested_profit_sharing: fixedOrBlank(emp.vestedProfitSharing),
    vested_employer_total: emp.knownVestedEmployerTotal.toFixed(2),
    unvested_employer_total: emp.knownUnvestedEmployerTotal.toFixed(2),
    unresolved_vesting_amount: emp.vestingUnresolvedAmount.toFixed(2),
    unresolved_vesting_sources: emp.vestingUnresolvedSources.join('; '),
    estimated_forfeiture: emp.estimatedForfeiture.toFixed(2),
    unresolved_forfeiture_amount: emp.forfeitureUnresolvedAmount.toFixed(2),
    // After-tax (mega-backdoor Roth)
    after_tax_contribution: emp.afterTaxContribution.toFixed(2),
    after_tax_limited_by_415c: emp.afterTaxLimitedBy415c ? 'Yes' : 'No',
    // Totals
    total_contribution: emp.totalContribution.toFixed(2),
    participation_status: formatParticipationStatus(emp.participationStatus),
    exceeds_415c: emp.exceeds415c ? 'Yes' : 'No',
  }))

  // Compute totals row
  const sum = (key) => calculatedEmployees.reduce(
    (total, employee) => total + (Number.isFinite(employee[key]) ? employee[key] : 0),
    0,
  )
  const avg = (key) => calculatedEmployees.length > 0 ? sum(key) / calculatedEmployees.length : 0

  const totalsRow = {
    id: 'TOTALS',
    name: `${calculatedEmployees.length} employees`,
    age: '',
    tenure: '',
    tenure_months: '',
    service_years: '',
    hours_served: '',
    compensation: sum('compensation').toFixed(2),
    cb_participant_entry_date: '',
    cb_participant_entry_date_source: '',
    cb_opening_balance: '',
    cb_prior_accrued_monthly_benefit: '',
    cb_prior_vesting_years: '',
    high_3_compensation: '',
    high_3_compensation_source: '',
    section_415_compensation: '',
    section_415_compensation_source: '',
    balance: sum('balance').toFixed(2),
    loan_balance: sum('loanBalance').toFixed(2),
    capped_compensation: sum('cappedCompensation').toFixed(2),
    is_hce: `${calculatedEmployees.filter(e => e.isHCE).length} HCE`,
    hce_is_estimated: `${calculatedEmployees.filter(e => e.hceIsEstimated).length} estimated`,
    is_key_employee: `${calculatedEmployees.filter(e => e.keyEmployeeStatus === 'key').length} Yes; ${calculatedEmployees.filter(e => e.keyEmployeeStatus === 'non-key').length} No; ${calculatedEmployees.filter(e => e.keyEmployeeStatus === 'unknown').length} Unknown`,
    allocation_group: '',
    comp_prorated: `${calculatedEmployees.filter(e => e.compWasProrated).length} prorated`,
    employment_status: '',
    data_warnings: `${calculatedEmployees.filter(e => e.warnings && e.warnings.length > 0).length} with warnings`,
    is_terminated: `${calculatedEmployees.filter(e => e.isTerminated).length} terminated`,
    eligibility_status: `${calculatedEmployees.filter(e => e.eligibility.status === 'eligible').length} Eligible; ${calculatedEmployees.filter(e => e.eligibility.status === 'ineligible').length} Ineligible; ${calculatedEmployees.filter(e => e.eligibility.status === 'unknown').length} Unknown`,
    meets_age_requirement: '',
    meets_service_requirement: '',
    meets_hours_requirement: '',
    pretax_percent: avg('pretaxPercent').toFixed(2),
    roth_percent: avg('rothPercent').toFixed(2),
    total_deferral_percent: avg('totalDeferralPercent').toFixed(2),
    pretax_contribution: sum('actualPretax').toFixed(2),
    roth_contribution: sum('actualRoth').toFixed(2),
    catchup_eligible: `${calculatedEmployees.filter(e => e.catchupEligible).length} eligible`,
    catchup_type: '',
    catchup_contribution: sum('catchupContribution').toFixed(2),
    total_employee_contribution: sum('totalEmployeeContribution').toFixed(2),
    employer_match: sum('matchAmount').toFixed(2),
    employer_nec: sum('necAmount').toFixed(2),
    employer_qnec: sum('qnecAmount').toFixed(2),
    allocated_forfeiture: sum('forfeitureAllocation').toFixed(2),
    employer_profit_sharing: sum('profitSharingAmount').toFixed(2),
    age_weighted_profit_sharing: sum('ageWeightedProfitSharingAmount').toFixed(2),
    age_weighted_testing_age: '',
    age_weighted_factor: '',
    profit_sharing_component_breakdown: '',
    profit_sharing_group: '',
    profit_sharing_415c_limited: `${calculatedEmployees.filter(e => e.profitSharingLimitedBy415c).length} limited`,
    employer_total: sum('dcEmployerCost').toFixed(2),
    vesting_enabled: '',
    vested_match_percent: '',
    vested_nec_percent: '',
    vested_profit_sharing_percent: '',
    vested_match: sum('vestedMatch').toFixed(2),
    vested_nec: sum('vestedNec').toFixed(2),
    vested_profit_sharing: sum('vestedProfitSharing').toFixed(2),
    vested_employer_total: sum('vestedEmployerTotal').toFixed(2),
    unvested_employer_total: sum('unvestedEmployerTotal').toFixed(2),
    unresolved_vesting_amount: sum('vestingUnresolvedAmount').toFixed(2),
    unresolved_vesting_sources: `${calculatedEmployees.filter(e => e.vestingUnresolvedAmount > 0).length} employees`,
    estimated_forfeiture: sum('estimatedForfeiture').toFixed(2),
    unresolved_forfeiture_amount: sum('forfeitureUnresolvedAmount').toFixed(2),
    after_tax_contribution: sum('afterTaxContribution').toFixed(2),
    after_tax_limited_by_415c: `${calculatedEmployees.filter(e => e.afterTaxLimitedBy415c).length} limited`,
    total_contribution: sum('totalContribution').toFixed(2),
    participation_status: `${calculatedEmployees.filter(e => e.participationStatus === 'participating' || e.participationStatus === 'maxed').length} participating`,
    exceeds_415c: `${calculatedEmployees.filter(e => e.exceeds415c).length} exceeded`,
  }

  data.push(totalsRow)
  return data
}

export function createCalculationsCsvArtifact(calculatedEmployees, { estimate = false } = {}) {
  const csv = Papa.unparse(buildCalculatedCsvData(calculatedEmployees), { escapeFormulae: true })
  return {
    content: csv,
    fileName: `401k-calculations-export${estimate ? '-ESTIMATE' : ''}.csv`,
    mimeType: 'text/csv',
  }
}

export function exportCalculationsToCSv(calculatedEmployees, options = {}) {
  const artifact = createCalculationsCsvArtifact(calculatedEmployees, options)
  downloadFile(artifact.content, artifact.fileName, artifact.mimeType)
}
